import {Button, OptionProps} from '@bosch/react-frok';
import React, {useContext, useEffect, useState} from 'react';
import {t} from 'i18next';
import {
    AddBar,
    CloseButtonWrapper,
    ContentWrapper,
    FiltersContainer,
    FiltersContent,
    FilterSelectionWrapper,
    FilterSortBar,
    FilterSortContainer,
    FilterSortContainerBox,
    MobileCommandBar,
    ResetChipsButtonContainer,
    StyledChipClickableArea,
    TotalCountContainer
} from './SearchBar.style';
import {isNullOrEmpty, isPredefinedFilterApplicable, isSearchFilterApplied, isValidSearchTerm} from '../../utils';
import {TranscodeService} from '../../services/transcode.service';
import {dynamicConfig} from '../../init/config';
import {AuthContext} from '../../context/AuthContext';
import {SortOrder} from '../../models/SortOrder';
import {objectTypeOptionResource} from '../ObjectTypePicker/resources/ObjectTypeResource';
import {
    getLocalizedFileTypeNameToOptionPropName,
    SELECT_TYPE,
    TYPE_ALL
} from '../ObjectTypePicker/resources/FileTypeResource';
import {ObjectTypePicker} from '../ObjectTypePicker/ObjectTypePicker';
import {hasPublicOnlyAccess} from '../../utils/UserUtils';
import {useIsMobile} from '../../hooks/UseIsMobile';
import {FileTypePickerAdvanced} from '../ObjectTypePicker/FileTypePickerAdvanced';
import {Valid} from '../validation/Valid';
import {getErrorDataValidationMessages, getSearchTermConstraintErrorMessage} from '../errors/ErrorRegister';
import {ErrorDialogue} from '../errors/ErrorDialogue';

export interface SearchBarComponentProps {
    readonly itemCount: number;
    readonly searchTermParam: string | null;
    readonly recentlyAddedDaysParam: number;
    readonly isFavoriteParam: boolean;
    readonly isPublicParam: boolean;
    readonly sortOrderParam: string;
    readonly objectTypeParam: string;
    readonly fileTypesParam: string[] | null;
    onFilterDisable: () => void;
    onSortItemsChange: (sortOrder: string) => void;
    onSearchTermChange: (searchTerm: string | null) => void;
    onFileTypesChange: (fileTypes: string[] | null) => void;
    onRecentlyAddedChange: (isRecentlyAdded: boolean, noOfDays: number) => void;
    onFavoriteChange: (isFavorite: boolean) => void;
    onPublicChange: (isPublic: boolean) => void;
    onObjectTypeChange: (objectType: string) => void;
    mainAction: React.ReactNode;
}

const getObjectTypeOptions = (): OptionProps[] => {
    return objectTypeOptionResource
        .map(type => {
            return {name: getLocalizedFileTypeNameToOptionPropName(type.name), value: type.value}
        });
};

/**
 * @name SearchBarComponent
 * @param { number } itemCount                                              Total count of fetched items in the parent component
 * @param { string } searchTermParam                                        The search term to the user entered initially
 * @param { number } recentlyAddedDaysParam                                 The search option for recently added files(folders) in days calculated from dateCreated
 * @param { boolean } isFavoriteParam                                       The search option for files/folders marked as favorite
 * @param { boolean } isPublicParam                                         The search option for declared for public access files(folders)
 * @param { string } sortOrderParam                                         The search option for order in alphabetical(reversed) mode SQL based
 * @param { string } objectTypeParam                                        The search option for object type(ALL, Folder or File)
 * @param { () => void } onSortItemsChange                                  Callback to sort (alphabetically) items
 * @param { () => void } onFilterDisable                                    Callback to disable all enabled filter keys
 * @param { (searchTerm: string) => void } onSearchTermChanged              Callback to set the search term
 * @param { (fileTypes: string[]) => void } onFiltersChange                 Callback to set the enabled filter keys
 * @param { (isRecentlyAdded: boolean) => void } onRecentlyAddedChange      Callback to set the recently added search option
 * @param { (isFavorite: boolean) => void } onFavoriteChange                Callback to set the favorites search option
 * @param { (isPublic: boolean) => void } onPublicChange                    Callback to set the public search option
 * @param { (objectType: string) => void } onObjectTypeChange               Callback to set the object type search option
 * @param { ReactNode } mainAction                                          React element to show a main action in the bar (currently used for adding items)
 * @description
 * The main page's search bar, corresponding and manipulating the view relative to any fetched items in the parent container
 */
export const SearchBarComponent: React.VFC<SearchBarComponentProps> = (props) => {
    const [dialogError, setDialogError] = useState<Partial<{ headline?: string, errorDescription?: string }>>();
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isPublicOnlyAccess] = useState<boolean>(hasPublicOnlyAccess(isAuthenticated, roles));
    const [searchTermInputValue, setSearchTermInputValue] = useState(props.searchTermParam ? decodeURI(props.searchTermParam).trim() : '');
    const [isRecentlyAdded, setIsRecentlyAdded] = useState<boolean>(props.recentlyAddedDaysParam > 0);
    const [isFavorite, setIsFavorite] = useState<boolean>(props.isFavoriteParam);
    const [isPublic, setIsPublic] = useState<boolean>(props.isPublicParam);
    const [sortOrder, setSortOrder] = useState(props.sortOrderParam ? props.sortOrderParam.toLowerCase() : SortOrder.ASC);
    const [objectType, setObjectType] = useState(props.objectTypeParam ? props.objectTypeParam.toLowerCase() : '');
    const [fileTypes, setFileTypes] = useState<string[]>(props.fileTypesParam ? props.fileTypesParam : []);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const isPredefinedFiltersApplicable = isPredefinedFilterApplicable(dynamicConfig.PREDEFINED_FILTERS);

    const isValidObjectTypeFilter = (value: string | undefined): boolean => {
        return ((value === undefined) || (isNullOrEmpty(value)) || (value.toLowerCase() === TYPE_ALL)) as boolean;
    };

    const isValidFileTypeFilter = (values: string[] | undefined): boolean => {
        return ((values === undefined) || (!values) || (values.length === 0)
            || ((values.length === 1) && ((isNullOrEmpty(values[0])) || (values[0] === SELECT_TYPE)))) as boolean;
    };

    const filtersCountTotal = () => {
        let filterCount = fileTypes.length;
        if (!isNullOrEmpty(searchTermInputValue)) {
            filterCount++;
        }
        if (isRecentlyAdded) {
            filterCount++;
        }
        if (isFavorite) {
            filterCount++;
        }
        if (isPublic) {
            filterCount++;
        }
        if (sortOrder && (sortOrder === SortOrder.DESC)) {
            filterCount++;
        }
        if (objectType && (objectType.toLowerCase() !== TYPE_ALL)) {
            filterCount++;
        }

        return filterCount;
    };

    useEffect(() => {
        if (!isFavorite) {
            handleDisableFavoriteFilter();
        }
    }, [isFavorite]);

    useEffect(() => {
        if (!isPublic) {
            handleDisablePublicFilter();
        }
    }, [isPublic]);

    useEffect(() => {
        if (isValidObjectTypeFilter(objectType)) {
            handleDisableObjectTypeFilter();
        }
    }, [props.objectTypeParam]);

    useEffect(() => {
        if (isValidFileTypeFilter(fileTypes)) {
            handleDisableFileTypeFilter();
        }
    }, [props.fileTypesParam]);

    const handleOnChangeSearchTerm = (searchTerm: string) => {
        let searchTermValue = decodeURI(searchTerm);
        if (searchTermValue.trim().length > 0) {
            setSearchTermInputValue(searchTermValue);
        } else {
            setSearchTermInputValue('');
        }
    };

    const handleEnableSearchTermFilter = () => {
        if (isValidSearchTermInputValue) {
            props.onSearchTermChange(searchTermInputValue.trim());
        } else {
            setDialogError(getErrorDataValidationMessages(t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT_HEADLINE'), getSearchTermConstraintErrorMessage()));
        }
    };

    const handleDisableSearchTermFilter = () => {
        setSearchTermInputValue('');
        props.onSearchTermChange('');
    };

    const handleEnableRecentlyAddedFilter = () => {
        props.onRecentlyAddedChange(!isRecentlyAdded, !isRecentlyAdded ? dynamicConfig.NO_OF_RECENT_DAYS : 0);
        setIsRecentlyAdded(!isRecentlyAdded);
    };

    const handleDisableRecentlyAddedFilter = () => {
        props.onRecentlyAddedChange(false, 0);
        setIsRecentlyAdded(false);
    };

    const handleEnableFavoriteFilter = () => {
        props.onFavoriteChange(!isFavorite);
        setIsFavorite(!isFavorite);
    };

    const handleDisableFavoriteFilter = () => {
        props.onFavoriteChange(false);
        setIsFavorite(false);
    };

    const handleSortOrderFilter = () => {
        let sortOrderStatus = ((sortOrder && (sortOrder === SortOrder.DESC)) ? SortOrder.ASC : SortOrder.DESC);
        props.onSortItemsChange(sortOrderStatus);
        setSortOrder(sortOrderStatus);
    };

    const handleDisableSortOrderFilter = () => {
        props.onSortItemsChange(SortOrder.ASC);
        setSortOrder(SortOrder.ASC);
    };

    const handleEnablePublicFilter = () => {
        props.onPublicChange(!isPublic);
        setIsPublic(!isPublic);
    };

    const handleDisablePublicFilter = () => {
        props.onPublicChange(false);
        setIsPublic(false);
    };

    const handleEnableObjectTypeFilter = (objectTypeSelection: string) => {
        props.onObjectTypeChange(objectTypeSelection);
        setObjectType(objectTypeSelection);
    };

    const handleDisableObjectTypeFilter = () => {
        props.onObjectTypeChange('');
        setObjectType('');
    };

    const handleEnableFileTypeFilter = (fileTypeSelection: string[]) => {
        if (fileTypeSelection && (fileTypeSelection.length > 0)) {
            props.onFileTypesChange(fileTypeSelection);
            setFileTypes(fileTypeSelection);
            return;
        }

        handleDisableFileTypeFilter();
    };

    const handleDisableFileTypeFilter = () => {
        props.onFileTypesChange([]);
        setFileTypes([]);
    };

    const handleDisableSingleFileTypeFilter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, fileTypeSelection: string) => {
        if (isNullOrEmpty(fileTypeSelection)) {
            handleDisableFileTypeFilter();
            return;
        }

        let fileTypeState = fileTypes.filter(type => (fileTypeSelection !== type));
        if ((!fileTypeState) || (fileTypeState.length === 0)) {
            handleDisableFileTypeFilter();
            return;
        }

        props.onFileTypesChange(fileTypeState);
        setFileTypes(fileTypeState);
    };

    const handleResetAllFilters = () => {
        setIsFiltersExpanded(false);
        handleDisableSearchTermFilter();
        handleDisableRecentlyAddedFilter();
        handleDisableFavoriteFilter();
        handleDisablePublicFilter();
        handleDisableObjectTypeFilter();
        handleDisableFileTypeFilter();
        handleDisableSortOrderFilter();
        props.onFilterDisable();
    };

    const handleApplyPredefinedFilters = () => {
        handleResetAllFilters();
        let predefinedFilters = dynamicConfig.PREDEFINED_FILTERS;
        if (predefinedFilters.searchParam && (!isNullOrEmpty(predefinedFilters.searchParam))) {
            handleOnChangeSearchTerm(predefinedFilters.searchParam);
            if (isValidSearchTermInputValue) {
                props.onSearchTermChange(predefinedFilters.searchParam);
            } else {
                setDialogError(getErrorDataValidationMessages(t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT_HEADLINE'), getSearchTermConstraintErrorMessage()));
            }
        }

        (predefinedFilters.fileTypes && (predefinedFilters.fileTypes.length > 0)) && handleEnableFileTypeFilter(predefinedFilters.fileTypes);
        (predefinedFilters.objectType && (predefinedFilters.objectType.toLowerCase() !== TYPE_ALL)) && handleEnableObjectTypeFilter(predefinedFilters.objectType);
    };

    const populateFilters = () => {
        return (
            <ContentWrapper>
                <ObjectTypePicker dataSource={getObjectTypeOptions()}
                                  defaultValue={objectType}
                                  resetState={isValidObjectTypeFilter(objectType)}
                                  resetInput={false}
                                  disabled={false}
                                  onChange={(objectTypeSelection: string) => handleEnableObjectTypeFilter(objectTypeSelection)}
                />
                <FileTypePickerAdvanced
                    resetState={isValidFileTypeFilter(fileTypes)}
                    resetInput={false}
                    fileTypes={fileTypes}
                    onChange={fileTypeSelection => handleEnableFileTypeFilter(fileTypeSelection)}
                />
                <FiltersContent>
                    <CloseButtonWrapper>
                        <Button icon='up'
                                label={t('FILTERS_AREA_CLOSE_FILTERS_LABEL')}
                                mode='secondary'
                                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}/>
                    </CloseButtonWrapper>
                </FiltersContent>
            </ContentWrapper>
        );
    };

    let isValidSearchTermInputValue = isValidSearchTerm(searchTermInputValue);
    let visibleResetButton = isSearchFilterApplied(searchTermInputValue, isRecentlyAdded, isFavorite, isPublic, sortOrder, objectType, fileTypes);

    const renderSelection = () => {
        return (
            <>
                {(!isNullOrEmpty(searchTermInputValue)) &&
                <StyledChipClickableArea buttonClose label={searchTermInputValue}
                                         onClick={handleDisableSearchTermFilter}/>}
                {(isRecentlyAdded) &&
                <StyledChipClickableArea buttonClose label={t('SEARCH_BAR_COMPONENT_RECENTLY_ADDED')}
                                         onClick={handleDisableRecentlyAddedFilter}/>}
                {(isFavorite) &&
                <StyledChipClickableArea buttonClose label={t('SEARCH_BAR_COMPONENT_FAVORITES')}
                                         onClick={handleDisableFavoriteFilter}/>}
                {(isPublic) &&
                <StyledChipClickableArea buttonClose label={t('SEARCH_BAR_COMPONENT_PUBLIC_ACCESS')}
                                         onClick={handleDisablePublicFilter}/>}
                {(sortOrder && (sortOrder.toLowerCase() === SortOrder.DESC)) &&
                <StyledChipClickableArea buttonClose
                                         label={t('SEARCH_BAR_COMPONENT_SORT_ORDER', {order: t('SORT_ORDER_DESC')})}
                                         onClick={handleSortOrderFilter}/>}
                {(objectType && (objectType.toLowerCase() !== TYPE_ALL)) &&
                <StyledChipClickableArea buttonClose
                                         label={t('SEARCH_BAR_COMPONENT_OBJECT_TYPE', {type: t('FILTER_TYPE_'.concat(objectType.toUpperCase()))})}
                                         onClick={handleDisableObjectTypeFilter}/>}
                {(fileTypes && (fileTypes.length > 0)) && fileTypes.map(type => (
                    <StyledChipClickableArea key={type}
                                             buttonClose
                                             label={type}
                                             onClick={(e) => handleDisableSingleFileTypeFilter(e, type)}/>
                ))}
                <ResetChipsButtonContainer>
                    {visibleResetButton && (
                        <Button
                            icon='reset'
                            label={t('SEARCH_BAR_COMPONENT_RESET_ALL_LABEL')}
                            mode='tertiary'
                            onClick={handleResetAllFilters}
                        />
                    )}
                    {isPredefinedFiltersApplicable && (
                        <Button icon='refresh'
                                label={t('FILTERS_AREA_APPLY_DEFAULT_LABEL')}
                                mode='tertiary'
                                onClick={handleApplyPredefinedFilters}
                        />
                    )}
                </ResetChipsButtonContainer>
            </>
        );
    };

    const renderErrorDialog = (populate: boolean, headline: string | undefined, description: string | undefined) => {
        return <ErrorDialogue populate={populate}
                              headline={headline}
                              description={description}
                              onChange={() => setDialogError(undefined)}
        />
    };

    return (
        <>
            {!isMobile ?
                <>
                    <Valid
                        isValid={isValidSearchTermInputValue}
                        message={t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT', {param1: 2})}>
                        <div key='searchField' className='a-text-field a-text-field--search'>
                            <input
                                className='searchInputField'
                                type='search'
                                id='15'
                                placeholder={t('SEARCH_BAR_COMPONENT_PLACEHOLDER_SEARCH_TERM')}
                                value={TranscodeService.decodeString(searchTermInputValue)}
                                onChange={(event) => handleOnChangeSearchTerm(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        handleEnableSearchTermFilter();
                                    }
                                }}
                            />
                            <button
                                type='submit'
                                className='a-text-field__icon-search'
                                onClick={handleEnableSearchTermFilter}
                            >
                                <i className='a-icon ui-ic-search' title={t('SEARCH_BAR_COMPONENT_TITLE')}/>
                            </button>
                        </div>
                    </Valid>
                    <FilterSortContainerBox>
                        <FilterSortBar>
                            <TotalCountContainer key='totalContainer' className='a-text'>
                                <h5 key='totalItems'>
                                    {props.itemCount} {t('SEARCH_BAR_COMPONENT_SEARCH_ITEM_COUNT')}
                                </h5>
                            </TotalCountContainer>
                            <FilterSortContainer key='buttonContainer' className='a-link a-link--simple'>
                                <Button
                                    className='filters-toggle'
                                    mode='tertiary'
                                    icon='filter'
                                    label={t('SEARCH_BAR_COMPONENT_FILTER_LABEL', {count: filtersCountTotal()})}
                                    onClick={() => {
                                        setIsFiltersExpanded(!isFiltersExpanded);
                                    }}
                                />
                                <Button mode='tertiary'
                                        icon={(isRecentlyAdded) ? 'clock-stop' : 'clock'}
                                        label={t('SEARCH_BAR_COMPONENT_RECENTLY_ADDED')}
                                        onClick={handleEnableRecentlyAddedFilter}/>

                                {(!isPublicOnlyAccess) &&
                                <Button mode='tertiary'
                                        icon={(isFavorite) ? 'wishlist-filled' : 'wishlist'}
                                        label={t('SEARCH_BAR_COMPONENT_FAVORITES')}
                                        onClick={handleEnableFavoriteFilter}/>}

                                {(!isPublicOnlyAccess) &&
                                <Button mode='tertiary'
                                        icon={(isPublic) ? 'lock-closed-checkmark' : 'lock-closed'}
                                        label={t('SEARCH_BAR_COMPONENT_PUBLIC_ACCESS')}
                                        onClick={handleEnablePublicFilter}/>}

                                <Button mode='tertiary'
                                        icon='sort-alphabetically'
                                        onClick={handleSortOrderFilter}/>
                            </FilterSortContainer>
                        </FilterSortBar>
                        <AddBar>
                            {props.mainAction}
                        </AddBar>
                    </FilterSortContainerBox>
                </>
                :
                <>
                    <Valid
                        isValid={isValidSearchTermInputValue}
                        message={t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT', {param1: 2})}>
                        <div key='searchField' className='a-text-field a-text-field--search'>
                            <input
                                className='searchInputField'
                                type='search'
                                id='15'
                                placeholder={t('SEARCH_BAR_COMPONENT_PLACEHOLDER_SEARCH_TERM')}
                                value={TranscodeService.decodeString(searchTermInputValue)}
                                onChange={(event) => handleOnChangeSearchTerm(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        handleEnableSearchTermFilter();
                                    }
                                }}
                            />
                            <button
                                type='submit'
                                className='a-text-field__icon-search'
                                onClick={handleEnableSearchTermFilter}
                            >
                                <i className='a-icon ui-ic-search' title={t('SEARCH_BAR_COMPONENT_TITLE')}/>
                            </button>
                        </div>
                    </Valid>
                    <MobileCommandBar>
                        <TotalCountContainer key='totalContainer' className='a-text'>
                            <h5 key='totalItems'>
                                {props.itemCount} {t('SEARCH_BAR_COMPONENT_SEARCH_ITEM_COUNT')}
                            </h5>
                        </TotalCountContainer>
                        <FilterSortContainer key='buttonContainer' className='a-link a-link--simple'>
                            <Button
                                className='filters-toggle'
                                mode='tertiary'
                                icon='filter'
                                label={t('SEARCH_BAR_COMPONENT_FILTER_LABEL', {count: filtersCountTotal()})}
                                onClick={() => {
                                    setIsFiltersExpanded(!isFiltersExpanded);
                                }}
                            />
                            <Button mode='tertiary'
                                    icon={(isRecentlyAdded) ? 'clock-stop' : 'clock'}
                                    onClick={handleEnableRecentlyAddedFilter}/>

                            {isAuthenticated &&
                            <Button mode='tertiary'
                                    icon={(isFavorite) ? 'wishlist-filled' : 'wishlist'}
                                    onClick={handleEnableFavoriteFilter}/>}

                            {isAuthenticated &&
                            <Button mode='tertiary'
                                    icon={(isPublic) ? 'lock-closed-checkmark' : 'lock-closed'}
                                    onClick={handleEnablePublicFilter}/>}

                            <Button mode='tertiary'
                                    icon='sort-alphabetically'
                                    onClick={handleSortOrderFilter}/>
                        </FilterSortContainer>
                        {props.mainAction}
                    </MobileCommandBar>
                </>
            }
            <FiltersContainer className='-secondary' expanded={isFiltersExpanded}>
                {populateFilters()}
            </FiltersContainer>
            <FilterSelectionWrapper>
                {renderSelection()}
            </FilterSelectionWrapper>
            {renderErrorDialog(!!dialogError, dialogError?.headline, dialogError?.errorDescription)}
        </>
    );
};
